import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';

/* ── Reveal hook ── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.rv');
    const ob = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('vis'); }),
      { threshold: 0.1 }
    );
    els.forEach(el => ob.observe(el));
    return () => ob.disconnect();
  }, []);
}

/* ────────────────────────────────── HERO ── */
function Hero() {
  const navigate = useNavigate();
  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="hero" className="hero">
      <video className='hero-video' autoPlay muted loop playsInline>
        <source src="/hero-video.mp4" type="video/mp4" />
      </video>
      <div className='hero-video-overlay' />
      <div className="hero-grid" />
      <div className="hero-orb hero-orb-1" />
      <div className="hero-orb hero-orb-2" />
      <div className="hero-line hl-1" />
      <div className="hero-line hl-2" />
      <div className="hero-line hl-3" />

      <div className="hero-content">
        <div className="hero-badge">
          <div className="hero-badge-dot" />
          <span>Information Services · Est. India</span>
        </div>

        <h1 className="hero-title">
          Empowering Business<br />
          Through <span className="hero-gold">Intelligence</span>
        </h1>

        <p className="hero-subtitle">
          Kelwa Technologies Private Limited delivers innovative information service
          solutions that transform how organisations operate, connect, and grow
          in a digital-first world.
        </p>

        <div className="hero-actions">
          <button className="btn-primary" onClick={() => go('services')}>
            <span>Explore Services</span><span>→</span>
          </button>
          <button className="btn-outline" onClick={() => go('contact')}>
            Contact Us
          </button>
        </div>
      </div>

      <div className="hero-scroll" onClick={() => go('about')}>
        <span>Scroll</span>
        <div className="scroll-line" />
      </div>
    </section>
  );
}

/* ────────────────────────────────── ABOUT ── */
function About() {
  const highlights = [
    { icon: '🏛️', title: 'Registered Entity',  desc: 'Incorporated under the Companies Act with full statutory compliance' },
    { icon: '🤝', title: 'Client-Centric',      desc: 'Solutions tailored to the unique needs of every engagement' },
    { icon: '📊', title: 'Data Integrity',       desc: 'Industry-leading data privacy and security standards' },
    { icon: '🔐', title: 'Confidential',         desc: 'Strict information security protocols on every mandate' },
  ];

  return (
    <section id="about" className="section-wrap about-section">
      <div className="section-inner">
        <div className="about-grid">

          {/* Visual card */}
          <div className="about-visual rv">
            <div className="about-card">
              <div className="about-card-watermark">KT</div>
              <div className="about-card-accent-1" />
              <div className="about-card-accent-2" />
              <div className="about-stat">10+</div>
              <div className="about-stat-label">Years of Combined Expertise</div>
            </div>
          </div>

          {/* Text content */}
          <div>
            <div className="eyebrow rv"><div className="eyebrow-line" /><span className="eyebrow-text">Our Story</span></div>
            <h2 className="sec-title rv d1">A Trusted Partner in Information Services</h2>
            <p className="about-text rv d2">
              Kelwa Technologies Private Limited is a professionally incorporated Indian company
              specialising in information service activities. We bridge the gap between complex
              data ecosystems and actionable business insights, serving clients across industries
              with precision and integrity.
            </p>
            <div className="about-highlights">
              {highlights.map((h, i) => (
                <div className={`about-highlight rv d${i + 2}`} key={i}>
                  <div className="about-highlight-icon">{h.icon}</div>
                  <div>
                    <strong>{h.title}</strong>
                    <span>{h.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────── SERVICES ── */
function Services() {
  const svcs = [
    { icon: '🔍', title: 'Data Research & Analytics',    desc: 'Deep-dive research and structured analytics to uncover business-critical insights from complex information landscapes.' },
    { icon: '📡', title: 'Information Management',        desc: 'End-to-end management of organisational information assets including capture, processing, storage and distribution.' },
    { icon: '🌐', title: 'Digital Information Services',  desc: 'Web-based platforms and portals that deliver structured content and information to end users at scale.' },
    { icon: '📑', title: 'Documentation & Compliance',    desc: 'Professional documentation services ensuring your business information meets regulatory and compliance standards.' },
    { icon: '🤖', title: 'Intelligent Automation',        desc: 'Automating information workflows to enhance speed, accuracy, and operational efficiency across business units.' },
    { icon: '💡', title: 'Strategic Consulting',          desc: 'Advisory services helping organisations design robust information strategies aligned with growth objectives.' },
  ];

  return (
    <section id="services" className="section-wrap services-section">
      <div className="services-bg-dots" />
      <div className="section-inner">
        <div className="services-header">
          <div>
            <div className="eyebrow rv"><div className="eyebrow-line" /><span className="eyebrow-text">What We Do</span></div>
            <h2 className="sec-title sec-title-light rv d1">Our Core Services</h2>
          </div>
          <p className="sec-desc sec-desc-light rv d2">
            A comprehensive suite of information service solutions designed for the modern enterprise.
          </p>
        </div>

        <div className="services-grid">
          {svcs.map((s, i) => (
            <div className="service-card rv" style={{ transitionDelay: `${i * 0.07}s` }} key={i}>
              <div className="service-num">0{i + 1}</div>
              <div className="service-icon">{s.icon}</div>
              <div className="service-title">{s.title}</div>
              <p className="service-desc">{s.desc}</p>
              <div className="service-arrow">Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────── PARALLAX BANNER ── */
function ParallaxBanner() {
  const bgRef = useRef(null);

  useEffect(() => {
    const fn = () => {
      const sec = document.querySelector('.parallax-banner');
      if (!sec || !bgRef.current) return;
      const r = sec.getBoundingClientRect();
      bgRef.current.style.transform = `translateY(${r.top * 0.38}px)`;
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <div className="parallax-banner">
      <div className="parallax-bg" ref={bgRef} />
      <div className="parallax-content">
        <p className="parallax-quote">
          "In the age of information, the ability to transform{' '}
          <span className="parallax-highlight">raw data into wisdom</span>{' '}
          is the ultimate competitive advantage."
        </p>
        <div className="parallax-divider" />
        <p className="parallax-attr">Kelwa Technologies · Our Philosophy</p>
      </div>
    </div>
  );
}

/* ────────────────────────────────── WHY US ── */
function WhyUs() {
  const items = [
    { n: '01', icon: '⚡', title: 'Rapid Delivery',     text: 'Agile methodologies ensure timely delivery without compromising quality or precision.' },
    { n: '02', icon: '🔬', title: 'Research-Backed',    text: 'Every solution is grounded in rigorous research and evidence-based methodologies.' },
    { n: '03', icon: '🌍', title: 'Scalable Solutions', text: 'Our frameworks scale seamlessly from startup to enterprise-level deployments.' },
    { n: '04', icon: '🛡️', title: 'Compliance First',   text: 'Full adherence to Indian data protection laws and international information security standards.' },
  ];

  return (
    <section id="why" className="section-wrap why-section">
      <div className="section-inner">
        <div className="eyebrow rv"><div className="eyebrow-line" /><span className="eyebrow-text">Why Choose Us</span></div>
        <h2 className="sec-title rv d1">The Kelwa Difference</h2>
        <div className="why-grid">
          {items.map((w, i) => (
            <div className={`why-card rv d${i + 1}`} key={i}>
              <div className="why-num">{w.n}</div>
              <div className="why-icon">{w.icon}</div>
              <div className="why-title">{w.title}</div>
              <p className="why-text">{w.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────── TEAM ── */
function Team() {
  const members = [
    { initials: 'RR', name: 'Rohit Reddy',   role: 'Founder & Director',      bio: 'Visionary leader with deep expertise in information management and enterprise technology strategy.' },
    { initials: 'AS', name: 'Anita Sharma',  role: 'Head of Operations',      bio: 'Operational excellence expert ensuring seamless service delivery across all client engagements.' },
    { initials: 'VP', name: 'Vivek Pandey',  role: 'Chief Technology Officer', bio: 'Technology architect building scalable, secure information platforms for modern businesses.' },
  ];

  return (
    <section id="team" className="section-wrap team-section">
      <div className="section-inner">
        <div className="eyebrow rv"><div className="eyebrow-line" /><span className="eyebrow-text">Our Team</span></div>
        <h2 className="sec-title sec-title-light rv d1">The Minds Behind Kelwa</h2>
        <div className="team-grid">
          {members.map((m, i) => (
            <div className={`team-card rv d${i + 1}`} key={i}>
              <div className="team-avatar">{m.initials}</div>
              <div className="team-name">{m.name}</div>
              <div className="team-role">{m.role}</div>
              <p className="team-bio">{m.bio}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────── CONTACT ── */
function Contact() {
  const [sent, setSent] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', email: '', company: '', subject: '', message: '' });
  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const submit = e => { e.preventDefault(); setTimeout(() => setSent(true), 300); };

  return (
    <section id="contact" className="section-wrap contact-section">
      <div className="section-inner">
        <div className="contact-grid">

          {/* Info */}
          <div>
            <div className="eyebrow rv"><div className="eyebrow-line" /><span className="eyebrow-text">Get In Touch</span></div>
            <h2 className="sec-title rv d1">Let's Start a Conversation</h2>
            <p className="sec-desc rv d2">
              Whether you have a project in mind or simply want to know more,
              our team is ready to assist you with expert guidance.
            </p>
            <div className="contact-info rv d3">
              {[
                { icon: '📍', label: 'Registered Office', value: 'India' },
                { icon: '📧', label: 'Email',             value: 'info@kelwatechnologies.in' },
                { icon: '📞', label: 'Telephone',         value: '+91 00000 00000' },
                { icon: '⏰', label: 'Business Hours',    value: 'Mon–Fri, 9:00 AM – 6:00 PM IST' },
              ].map((c, i) => (
                <div className="contact-item" key={i}>
                  <div className="contact-item-icon">{c.icon}</div>
                  <div>
                    <strong>{c.label}</strong>
                    <span>{c.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="contact-form rv d2">
            {sent ? (
              <div className="form-success">
                <div className="form-success-icon">✓</div>
                <strong>Thank you for reaching out.</strong>
                <p>A member of our team will be in touch within one business day.</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input name="name" required value={form.name} onChange={h} placeholder="Your full name" />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input name="email" type="email" required value={form.email} onChange={h} placeholder="you@company.com" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Company</label>
                    <input name="company" value={form.company} onChange={h} placeholder="Your organisation" />
                  </div>
                  <div className="form-group">
                    <label>Subject *</label>
                    <select name="subject" required value={form.subject} onChange={h}>
                      <option value="">Select a topic</option>
                      <option>General Enquiry</option>
                      <option>Service Request</option>
                      <option>Partnership</option>
                      <option>Careers</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Message *</label>
                  <textarea name="message" required value={form.message} onChange={h} placeholder="Tell us about your requirements..." />
                </div>
                <div className="form-submit">
                  <button type="submit" className="btn-primary">
                    <span>Send Message</span><span>→</span>
                  </button>
                </div>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────── HOME PAGE ── */
export default function Home() {
  useReveal();
  return (
    <>
      <Hero />
      <About />
      <Services />
      <ParallaxBanner />
      <WhyUs />
      <Team />
      <Contact />
    </>
  );
}
